import {
  Box,
  Grid,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,

  Stack,
  HStack,
  Divider,
  Center,
} from '@chakra-ui/react';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from 'auth/AuthProvider';
import { useCurrentUser } from 'auth/CurrentUserProvider';
import { BigBadge } from 'components/BigBadge';
import GitHubButton from 'components/GitHubButton';
import GradientTooltip from 'components/GradientTooltip';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Logo from 'components/Logo';
import { DiscordAttachModal } from 'components/Modal/DiscordAttachModal';
import LegacyPromoModal from 'components/Modal/LegacyPromoModal';
import Text from 'components/Text';
import { GITHUB_COOKIE_KEY } from 'config/constants/github';
import { StorageVars } from 'config/constants/storage';
import OnboardingMissions from 'features/onboarding-missions/OnboardingMissions';
import { Plan_Type_Enum } from 'types/databaseEnums';
import { EdgeFunctionName, UpdateGithubAccessTokenParams } from 'types/edgeFunctions';
import { ERoute } from 'types/frontend';
import { deleteCookie, getCookie } from 'utils/cookies';
import { openDiscord } from 'utils/discord';

import { useMediaQueries } from './MediaQueriesProvider';
import FeedbackButton from './Navigation/FeedbackButton';
import Support from './Navigation/Support';
import UserBigtokens from './Navigation/UserBigtokens';
import UserDropdownMenu from './Navigation/UserDropdownMenu';
import UserNotifications from './Navigation/UserNotifications';
import { SidebarContext } from './SidebarContext';

interface ILinkWrapperProps {
  isActive: boolean;
  isCollapsed: boolean;
  isLocked: boolean;
  children: ReactNode;
}

const LinkWrapper = ({ isCollapsed, isActive, isLocked, children }: ILinkWrapperProps) => {
  const isActiveProps =
    isCollapsed && isActive
      ? { bg: 'purple.400', borderRadius: 8 }
      : !isCollapsed && isActive
      ? { bg: 'purple.400', borderRadius: 8 }
      : {};

  return (
    <Box
      width="100%"
      opacity={isLocked ? 0.6 : isActive ? 1 : 0.87}
      _hover={{ opacity: isLocked ? 0.6 : 1 }}
      transition="all 0.2s ease-out"
      mb={isCollapsed ? 4 : 0}
      height={isCollapsed ? '32px' : 'initial'}
      pl={isCollapsed ? 1 : 2}
      pt={isCollapsed ? 1.5 : 0}
      lineHeight="32px"
      {...isActiveProps}
    >
      {children}
    </Box>
  );
};

interface INavigationLinkProps {
  children?: ReactNode;
  to?: ERoute | string;
  onClick?: () => void;
  isCollapsed?: boolean;
  isLocked?: boolean;
}

const NavigationLink = ({ to, children, isCollapsed, isLocked, onClick }: INavigationLinkProps) => {
  if (to) {
    return (
      <NavLink to={to} style={{ width: '100%', height: '32px' }}>
        {({ isActive }) => (
          <LinkWrapper isActive={isActive} isLocked={!!isLocked} isCollapsed={!!isCollapsed}>
            {children}
          </LinkWrapper>
        )}
      </NavLink>
    );
  }

  if (onClick) {
    return (
      <Box onClick={onClick} cursor="pointer" width={isCollapsed ? '32px' : '100%'}>
        <LinkWrapper isActive={false} isLocked={!!isLocked} isCollapsed={!!isCollapsed}>
          {children}
        </LinkWrapper>
      </Box>
    );
  }

  return (
    <Box width="100%" height="32px">
      <LinkWrapper isActive={false} isLocked={!!isLocked} isCollapsed={!!isCollapsed}>
        <Box width={isCollapsed ? '32px' : '100%'}>{children}</Box>
      </LinkWrapper>
    </Box>
  );
};

interface SidebarItemProps {
  icon: ReactNode;
  title: string;
  isCollapsed: boolean;
  isLocked?: boolean;
  lockTooltip?: string;
  navigationProps: INavigationLinkProps;
  onClose?: () => void;
}

const SidebarItem = ({
  title,
  icon,
  isCollapsed,
  isLocked,
  lockTooltip = 'Not available',
  navigationProps,
  onClose,
}: SidebarItemProps) => {
  return (
    <NavigationLink {...navigationProps} isLocked={isLocked} isCollapsed={isCollapsed}>
      <span onClick={() => onClose?.()}>
        <GradientTooltip label={isLocked ? lockTooltip : null}>
          <HStack mb={4} width="100%">
            <HStack width="100%" spacing={2} transition="all 0.2s ease-out">
              <GradientTooltip label={!isLocked && isCollapsed ? title : null}>
                <span style={{ marginLeft: isCollapsed ? 4 : 0 }}>{icon}</span>
              </GradientTooltip>
              {!isCollapsed && <Text fontSize="small">{title}</Text>}
            </HStack>
          </HStack>
        </GradientTooltip>
      </span>
    </NavigationLink>
  );
};

interface ISidebarContent {
  isDrawer?: boolean;
  isFreeUser: boolean;
  isCollapsed: boolean;
  isSignedIn: boolean;
  hasDiscordRegistered: boolean;
  onClose?: () => void;
  onToggleSidebar?: () => void;
}

const SidebarContent = ({
  isDrawer,
  isFreeUser,
  isCollapsed,
  isSignedIn,
  hasDiscordRegistered,
  onClose,
  onToggleSidebar,
}: ISidebarContent) => {
  const discordAttachModal = useDisclosure();
  const { isLegacy } = useCurrentUser();

  // Lock premium features for unauthenticated and expired free users (not legacy)
  const isPremiumLocked = !isSignedIn || (isFreeUser && !isLegacy);
  // Lock BigToken-dependent features (CV Builder, Prototypes) for ALL free users including legacy
  const isBigTokenLocked = !isSignedIn || isFreeUser;
  const lockTooltip = 'Not available';

  return (
    <>
      <Stack
        p={isDrawer ? 0 : isCollapsed ? 5 : 6}
        alignItems={isCollapsed ? 'center' : 'flex-start'}
        width="100%"
        height="100%"
        position="relative"
      >
        <Box mb={isCollapsed ? 12 : 8} width={isCollapsed ? 'auto' : '100%'}>
          {!isDrawer && (
            isCollapsed ? (
              <Box position="absolute" left="50%" top={4} transform="translateX(-50%)" ml={1}>
                <Logo isClickable rocketOnly size={24} />
              </Box>
            ) : (
              <Logo isClickable size={32} />
            )
          )}
        </Box>
        <SidebarItem
          title="Dashboard"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked={isPremiumLocked}
          lockTooltip={lockTooltip}
          navigationProps={!isPremiumLocked ? { to: ERoute.Dashboard } : {}}
          onClose={() => onClose?.()}
        />
        <Text fontSize="small" fontWeight="semibold" mt={2} opacity="secondary">
          Learn
        </Text>
        <SidebarItem
          title="Learning Paths"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked
          lockTooltip="Coming Soon"
          navigationProps={{}}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Projects"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked={isPremiumLocked}
          lockTooltip={lockTooltip}
          navigationProps={!isPremiumLocked ? { to: ERoute.Projects } : {}}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Challenges"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked={isPremiumLocked}
          lockTooltip={lockTooltip}
          navigationProps={!isPremiumLocked ? { to: ERoute.Challenges } : {}}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Problems"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked={isPremiumLocked}
          lockTooltip={lockTooltip}
          navigationProps={!isPremiumLocked ? { to: ERoute.PracticeProblems } : {}}
          onClose={() => onClose?.()}
        />
        <Text fontSize="small" fontWeight="semibold" mt={2} opacity="secondary">
          Build
        </Text>
        <SidebarItem
          title="Prototypes"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked={isBigTokenLocked}
          lockTooltip={isBigTokenLocked ? 'Upgrade to unlock' : undefined}
          navigationProps={!isBigTokenLocked ? { to: ERoute.Prototypes } : {}}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Code Frames"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked={isPremiumLocked}
          lockTooltip={lockTooltip}
          navigationProps={!isPremiumLocked ? { to: ERoute.CodeFrames } : {}}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Roast My Project"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 6.51 6.51 0 007.5 10.5a6.5 6.5 0 006.5-6.5c0-.592-.082-1.166-.238-1.714z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          navigationProps={{ to: ERoute.Roasts }}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Portfolio Builder"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked
          lockTooltip="Coming Soon"
          navigationProps={{}}
          onClose={() => onClose?.()}
        />
        <Text fontSize="small" fontWeight="semibold" mt={2} opacity="secondary">
          Career
        </Text>
        <SidebarItem
          title="CV Builder"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked={isBigTokenLocked}
          lockTooltip={isBigTokenLocked ? 'Upgrade to unlock' : undefined}
          navigationProps={isBigTokenLocked ? {} : { to: ERoute.CVBuilder }}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Interview Simulator"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked
          lockTooltip="Coming Soon"
          navigationProps={{}}
          onClose={() => onClose?.()}
        />
        <SidebarItem
          title="Remote Job Boards"
          icon={
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
          }
          isCollapsed={isCollapsed}
          isLocked
          lockTooltip="Coming Soon"
          navigationProps={{}}
          onClose={() => onClose?.()}
        />
        <Divider width="100%" />
        {isSignedIn && (
          <SidebarItem
            title="Plans"
            icon={
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
              </svg>
            }
            isCollapsed={isCollapsed}
            navigationProps={{ to: ERoute.Plans }}
            onClose={() => onClose?.()}
          />
        )}
        <SidebarItem
          title="Community"
          icon={<Icon type="SidebarCommunityDiscord" size={20} />}
          isCollapsed={isCollapsed}
          isLocked={isPremiumLocked}
          lockTooltip={lockTooltip}
          navigationProps={
            !isPremiumLocked
              ? {
                  onClick: hasDiscordRegistered ? openDiscord : discordAttachModal.onOpen,
                }
              : {}
          }
        />
        {!isDrawer && onToggleSidebar && (
          <Center width="100%" mt={4}>
            <IconButton
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              icon={
                <Box transform={isCollapsed ? 'rotate(180deg)' : undefined} transition="transform 0.2s" display="flex">
                  <Icon type="BackArrow" size={12} />
                </Box>
              }
              size="xs"
              variant="ghost"
              opacity={0.5}
              backgroundColor="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.100"
              _hover={{ opacity: 1, backgroundColor: 'whiteAlpha.100', borderColor: 'whiteAlpha.200' }}
              transition="all 0.2s"
              onClick={onToggleSidebar}
              w="28px"
              h="28px"
              minW="28px"
              borderRadius="md"
            />
          </Center>
        )}
        <Center
          width="100%"
          height="100%"
          left="50%"
          transform="translateX(-50%)"
          position="relative"
          bottom={0}
          ml={!isCollapsed && !isDrawer ? -1.5 : 0}
          alignItems="flex-end"
          justifyContent={isDrawer ? 'flex-start' : 'center'}
        >
          <Stack
            spacing={4}
            direction={isCollapsed ? 'column' : 'row'}
            px={3}
            py={1}
            mt={6}
            ml={isDrawer ? 4 : 0}
            borderRadius={8}
            backgroundColor={isCollapsed ? 'initial' : 'whiteAlpha.200'}
            justifyContent="center"
            width="fit-content"
            alignItems="center"
          >
            <Link href="https://www.instagram.com/bigdevsoon/" isExternal>
              <IconButton
                aria-label="Instagram"
                icon={<Icon type="SidebarInstagram" />}
                size="small"
                variant="ghost"
                opacity={0.6}
                transition="all 0.2 ease-out"
                _hover={{ opacity: 1 }}
              />
            </Link>
            <Link href="https://www.tiktok.com/@bigdevsoon" isExternal>
              <IconButton
                aria-label="TikTok"
                icon={<Icon type="SidebarTikTok" />}
                size="small"
                variant="ghost"
                opacity={0.6}
                transition="all 0.2 ease-out"
                _hover={{ opacity: 1 }}
              />
            </Link>
            <Link href="https://www.facebook.com/BigDevSoon" isExternal>
              <IconButton
                aria-label="Facebook"
                icon={<Icon type="SidebarFacebook" />}
                size="small"
                variant="ghost"
                opacity={0.6}
                transition="all 0.2 ease-out"
                _hover={{ opacity: 1 }}
              />
            </Link>
            <Link href="https://www.linkedin.com/company/bigdevsoon" isExternal>
              <IconButton
                aria-label="LinkedIn"
                icon={<Icon type="SidebarLinkedin" size={16} />}
                size="small"
                variant="ghost"
                opacity={0.6}
                transition="all 0.2 ease-out"
                _hover={{ opacity: 1 }}
              />
            </Link>
          </Stack>
        </Center>
      </Stack>
      <DiscordAttachModal isOpen={discordAttachModal.isOpen} onClose={discordAttachModal.onClose} />
    </>
  );
};

const ExpiredBanner = () => {
  const { pathname } = useLocation();
  const isPricingPage = pathname === ERoute.Plans || pathname === ERoute.UnlockBig;

  return (
    <Box
      bg="orange.700"
      px={4}
      py={2}
      mb={4}
      borderRadius={8}
    >
      <HStack justifyContent="center" spacing={1} flexWrap="wrap">
        <Text fontSize="small" fontWeight="regular">
          Your free trial has ended — you have limited access to the app.
        </Text>
        {!isPricingPage && (
          <Link to={ERoute.Plans} color="white" fontWeight="semibold" fontSize="small" textDecoration="underline">
            Upgrade to unlock all features
          </Link>
        )}
      </HStack>
    </Box>
  );
};

interface IAppLayoutProps {
  isFreeUser: boolean;
  hasDiscordRegistered: boolean;
  children: ReactNode;
}

export const AppLayout = ({ isFreeUser, hasDiscordRegistered, children }: IAppLayoutProps) => {
  const { userId } = useAuth();
  const { isSmallScreen, isDesktopHome } = useMediaQueries();
  const { isTerminated, isSuperUser, isLegacy } = useCurrentUser();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isSidebarManuallyCollapsed, setIsSidebarManuallyCollapsed] = useState(() => {
    try {
      return localStorage.getItem(StorageVars.BDS_SIDEBAR_COLLAPSED) === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setIsSidebarManuallyCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(StorageVars.BDS_SIDEBAR_COLLAPSED, String(next));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  const baseSidebarState = isSmallScreen ? 'drawer' : isDesktopHome ? 'full' : 'collapsed';
  // Allow user to collapse full sidebar or expand collapsed sidebar
  const sidebarState =
    baseSidebarState === 'drawer'
      ? 'drawer'
      : baseSidebarState === 'full'
        ? isSidebarManuallyCollapsed ? 'collapsed' : 'full'
        : isSidebarManuallyCollapsed ? 'full' : 'collapsed';

  const sidebarContextValue = useMemo(
    () => ({
      toggleSidebar,
      isSidebarCollapsed: sidebarState === 'collapsed' || sidebarState === 'drawer',
    }),
    [toggleSidebar, sidebarState]
  );

  const supabase = useSupabaseTypedClient();

  const isSignedIn = !!userId;

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }

    const accessToken = getCookie(GITHUB_COOKIE_KEY);

    if (accessToken) {
      supabase.functions.invoke(EdgeFunctionName.UpdateGithubAccessToken, {
        body: { accessToken } as UpdateGithubAccessTokenParams,
      });

      deleteCookie(GITHUB_COOKIE_KEY);
    }
  }, [isSignedIn]);

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
    <Grid
      templateColumns={
        sidebarState === 'drawer' ? '1fr' : sidebarState === 'full' ? '241px 1fr' : '75px 1fr'
      }
      templateRows="60px 1fr"
      height="100vh"
    >
      {sidebarState !== 'drawer' && (
        <Box
          gridColumn="1"
          gridRow="1 / 3"
          borderRight="1px solid"
          borderColor="whiteAlpha.300"
          position="fixed"
          height="100%"
          overflowY="auto"
          width={sidebarState === 'full' ? '241px' : '75px'}
          transition="width 0.2s ease"
        >
          <SidebarContent
            isCollapsed={sidebarState === 'collapsed'}
            isSignedIn={isSignedIn}
            isFreeUser={isFreeUser}
            hasDiscordRegistered={hasDiscordRegistered}
            onToggleSidebar={toggleSidebar}
          />
        </Box>
      )}

      <Box
        gridColumn={sidebarState === 'drawer' ? '1' : '2'}
        gridRow="1"
        borderBottom="1px solid"
        borderBottomColor="whiteAlpha.300"
        display="flex"
        alignItems="center"
        justifyContent={sidebarState === 'drawer' ? 'space-between' : 'flex-end'}
        pr={sidebarState === 'drawer' ? 6 : 4}
        pl={4}
        zIndex="docked"
        width="100%"
      >
        {sidebarState === 'drawer' && (
          <HStack spacing={2}>
            <IconButton
              aria-label="Open Sidebar"
              icon={<Icon type="SidebarHamburgerMenu" />}
              onClick={onOpen}
              variant="ghost"
            />
            <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="full">
              <DrawerOverlay />
              <DrawerContent bg="gray.900">
                <DrawerCloseButton mt={1} />
                <DrawerHeader pb={0} pl={8}>
                  <Box onClick={onClose}>
                    <Logo isClickable />
                  </Box>
                </DrawerHeader>
                <DrawerBody>
                  <SidebarContent
                    isDrawer
                    onClose={onClose}
                    isCollapsed={false}
                    isSignedIn={isSignedIn}
                    isFreeUser={isFreeUser}
                    hasDiscordRegistered={hasDiscordRegistered}
                  />
                </DrawerBody>
              </DrawerContent>
            </Drawer>
            <Box pt={0.5}>
              <Logo rocketOnly isClickable size={24} />
            </Box>
          </HStack>
        )}
        {isSignedIn ? (
          <HStack spacing={3}>
            <FeedbackButton />
            {!isSmallScreen && <Support />}
            <UserNotifications />
            <UserBigtokens />
            <UserDropdownMenu />
            {isFreeUser && !isSmallScreen && (
              <BigBadge
                plan={Plan_Type_Enum.Lifetime}
                isTerminated={isTerminated}
                isSuperUser={isSuperUser}
              />
            )}
          </HStack>
        ) : (
          <GitHubButton />
        )}
      </Box>
      <Box
        gridColumn={sidebarState === 'drawer' ? '1' : '2'}
        gridRow="2"
        px={sidebarState === 'drawer' ? 2 : 4}
        py={4}
        pb="60px"
        overflowY="auto"
        flexGrow={1}
        width="100%"
        minWidth="100%"
        height="calc(100vh - 60px)"
        id="big-dev-soon-main-scroller"
      >
        {isSignedIn && isFreeUser && !isLegacy && <ExpiredBanner />}
        {children}
      </Box>
      {isSignedIn && <OnboardingMissions />}
      {isSignedIn && <LegacyPromoModal />}
    </Grid>
    </SidebarContext.Provider>
  );
};
